import { z } from 'zod';

/**
 * Schema for instantie count response from get_instanties_with_counts RPC.
 * Used to populate the court filter in search.
 */
export const InstantieWithCountSchema = z.object({
  id: z.string().min(1),
  naam: z.string(),
  afkorting: z.string(),
  niveau: z.string(), // instantie_niveau enum
  locatie: z.string().nullable(),
  aangemaakt_op: z.string(),
  bijgewerkt_op: z.string(),
  case_count: z.number(),
});

export type InstantieWithCount = z.infer<typeof InstantieWithCountSchema>;

/**
 * Schema for zaak type count response from get_zaak_types_with_counts RPC.
 * Used to populate the case type filter in search.
 */
export const ZaakTypeWithCountSchema = z.object({
  id: z.string().min(1),
  code: z.string(),
  naam: z.string(),
  omschrijving: z.string().nullable(),
  aangemaakt_op: z.string(),
  bijgewerkt_op: z.string(),
  case_count: z.number(),
});

export type ZaakTypeWithCount = z.infer<typeof ZaakTypeWithCountSchema>;

/**
 * Schema for thema count response from get_thema_counts RPC.
 * Used to populate the thema filter in search.
 *
 * Note: The frontend transforms this into a hierarchical structure
 * using parent_id relationships.
 *
 * Field names match the SQL function return type:
 * - thema_id (not id)
 * - thema_naam (not naam)
 * - item_case_count (not count)
 */
export const ThemaCountSchema = z.object({
  thema_id: z.string().min(1),
  thema_naam: z.string(),
  parent_id: z.string().min(1).nullable(),
  item_case_count: z.number(),
});

export type ThemaCount = z.infer<typeof ThemaCountSchema>;

/**
 * Schema for wetsartikel hierarchy response from get_wetsartikelen_hierarchy_with_counts RPC.
 * Three-level hierarchy: Wetboek → Artikel → Individual References
 */
export const WetsartikelHierarchyItemSchema = z.object({
  wetboek_afkorting: z.string(),
  artikel_nummer: z.string(),
  count: z.number(),
  items: z.array(z.object({
    id: z.string().min(1),
    volledige_referentie: z.string(),
    count: z.number(),
  })),
});

export type WetsartikelHierarchyItem = z.infer<typeof WetsartikelHierarchyItemSchema>;

/**
 * Schema for rechtsgebied count (if using separate RPC).
 */
export const RechtsgebiedWithCountSchema = z.object({
  id: z.string().min(1),
  naam: z.string(),
  parent_id: z.string().min(1).nullable(),
  case_count: z.number(),
});

export type RechtsgebiedWithCount = z.infer<typeof RechtsgebiedWithCountSchema>;

// ============================================================================
// Validation Functions
// ============================================================================

/**
 * Validates array of instantie counts.
 */
export function validateInstantiesWithCounts(data: unknown): InstantieWithCount[] {
  return z.array(InstantieWithCountSchema).parse(data);
}

/**
 * Validates array of zaak type counts.
 */
export function validateZaakTypesWithCounts(data: unknown): ZaakTypeWithCount[] {
  return z.array(ZaakTypeWithCountSchema).parse(data);
}

/**
 * Validates array of thema counts.
 */
export function validateThemaCounts(data: unknown): ThemaCount[] {
  return z.array(ThemaCountSchema).parse(data);
}

/**
 * Safe parse for instantie counts.
 */
export function safeParseInstantiesWithCounts(data: unknown) {
  return z.array(InstantieWithCountSchema).safeParse(data);
}

/**
 * Safe parse for thema counts.
 */
export function safeParseThemaCounts(data: unknown) {
  return z.array(ThemaCountSchema).safeParse(data);
}
